'use client';

import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@kit/ui/alert';

import { TrainerCard } from './trainer-card';

export interface TrainerResource {
  id: string;
  name: string;
  bio?: string | null;
  picture_url?: string | null;
  normal_price: number;
  peak_price: number;
  member_price?: number | null;
  /** Phase B: server-resolved price (peak or normal per this slot). */
  original_price?: number | null;
  /** Phase B: server-resolved final price (member + perk baked in). */
  final_price?: number | null;
  per_person_addon_id?: string | null;
  per_person_price?: number | null;
  max_participants?: number;
}

interface TrainerSelectionProps {
  /** Available trainers from the time slot data */
  trainers: TrainerResource[];
  /** Whether current slot is peak time */
  isPeakTime: boolean;
  /** Whether user is a venue member (for member pricing) */
  isMember?: boolean;
  selectedTrainerId: string;
  onTrainerSelect: (trainerId: string, trainerPrice: number, isPeak: boolean) => void;
}

export function TrainerSelection({
  trainers,
  isPeakTime,
  isMember = false,
  selectedTrainerId,
  onTrainerSelect,
}: TrainerSelectionProps) {
  const { t } = useTranslation();

  if (trainers.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('bookings:noTrainersAvailable')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {trainers.map((trainer) => {
          // Phase B: prefer the server-resolved final_price. Fall back to
          // the legacy member/peak/normal pick for older payloads.
          const trainerPrice =
            trainer.final_price ??
            ((isMember && trainer.member_price != null)
              ? trainer.member_price
              : (isPeakTime ? trainer.peak_price : trainer.normal_price));

          return (
            <TrainerCard
              key={trainer.id}
              trainer={{
                id: trainer.id,
                name: trainer.name,
                bio: trainer.bio,
                picture_url: trainer.picture_url,
                normal_price: trainer.normal_price,
                peak_price: trainer.peak_price,
                applicable_price: trainerPrice,
                per_person_price: trainer.per_person_price,
                max_participants: trainer.max_participants,
              }}
              isSelected={selectedTrainerId === trainer.id}
              onSelect={() => onTrainerSelect(trainer.id, trainerPrice, isPeakTime)}
              isPeak={isPeakTime}
            />
          );
        })}
    </div>
  );
}